'use client';
import React from 'react';
import { useFormReturn } from 'react-hook-form';
import type { CampaignFormData } from './CampaignBuilderClient';

interface Props { form: useFormReturn<CampaignFormData>; }

const budgetPresets = [
  { id: 'bp-100', value: 100000, label: '100 000 DA' },
  { id: 'bp-250', value: 250000, label: '250 000 DA' },
  { id: 'bp-500', value: 500000, label: '500 000 DA' },
  { id: 'bp-1m', value: 1000000, label: '1 000 000 DA' },
  { id: 'bp-2m', value: 2000000, label: '2 000 000 DA' },
  { id: 'bp-5m', value: 5000000, label: '5 000 000 DA' },
];

export default function StepBudget({ form }: Props) {
  const { register, watch, setValue, formState: { errors } } = form;
  const budgetTotal = watch('budgetTotal');
  const budgetPerCreator = watch('budgetPerCreator');

  const estimatedCreators = budgetPerCreator > 0 ? Math.floor(budgetTotal / budgetPerCreator) : 0;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-700 text-foreground mb-1">Budget de campagne</h2>
        <p className="text-sm text-muted-foreground">Définissez votre enveloppe budgétaire pour cette campagne.</p>
      </div>

      {/* Total Budget */}
      <div>
        <label className="block text-sm font-600 text-foreground mb-1">
          Budget total <span className="text-negative">*</span>
        </label>
        <p className="text-xs text-muted-foreground mb-2">Enveloppe globale de la campagne (en Dinars Algériens)</p>
        <div className="relative">
          <input
            type="number"
            {...register('budgetTotal', { required: true, min: 10000 })}
            className="w-full bg-input border border-border rounded-lg px-4 py-3 pr-16 text-sm text-foreground outline-none focus:border-primary transition-colors font-mono"
          />
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-600 text-muted-foreground">DA</span>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {budgetPresets.map(bp => (
            <button
              key={bp.id}
              type="button"
              onClick={() => setValue('budgetTotal', bp.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-500 transition-all ${
                budgetTotal === bp.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {bp.label}
            </button>
          ))}
        </div>
      </div>

      {/* Budget per creator */}
      <div>
        <label className="block text-sm font-600 text-foreground mb-1">Budget par créateur</label>
        <p className="text-xs text-muted-foreground mb-2">Fourchette de rémunération par créateur</p>
        <div className="relative">
          <input
            type="number"
            {...register('budgetPerCreator', { min: 5000 })}
            className="w-full bg-input border border-border rounded-lg px-4 py-3 pr-16 text-sm text-foreground outline-none focus:border-primary transition-colors font-mono"
          />
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-600 text-muted-foreground">DA</span>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-muted/50 border border-border rounded-xl p-5 space-y-3">
        <h3 className="text-sm font-700 text-foreground">Récapitulatif budgétaire</h3>
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Budget total</span>
          <span className="text-sm font-700 text-foreground font-mono">{budgetTotal?.toLocaleString('fr-DZ')} DA</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Budget par créateur</span>
          <span className="text-sm font-700 text-foreground font-mono">{budgetPerCreator?.toLocaleString('fr-DZ')} DA</span>
        </div>
        <div className="flex items-center justify-between border-t border-border pt-3">
          <span className="text-sm font-600 text-foreground">Créateurs estimés</span>
          <span className="text-lg font-800 text-primary font-mono">{estimatedCreators}</span>
        </div>
      </div>
    </div>
  );
}